import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import ReactDOM from 'react-dom';
import { App } from '@capacitor/app';
import { locationData, SPECIAL_WISHES_POST_TYPE } from '../utils/constants';
import Select from 'react-select';

import NewsCard from '../components/newsCard';
import WishesCard from '../components/WishesCard';
import AdCard from '../components/AdCard';

// --- NEW COMPONENT IMPORTS ---
import QuotesCard from '../components/QuotesCard';
import AdRecommendedCard from '../components/AdRecommendedCard';

import { injectMixedContent } from '../utils/adInjector';
import apiClient from '../utils/api';

// --- FIREBASE IMPORTS FOR QUOTES SYNC ---
import { syncQuotesFromFirestore } from '../utils/quotesData';
import { getFirestore, collection, onSnapshot } from 'firebase/firestore';
import { app } from '../firebase-config';

import './ViewAllPage.css';

// Card boundary guard to secure render reliability
class CardErrorBoundary extends React.Component {
    constructor(props) { super(props); this.state = { hasError: false }; }
    static getDerivedStateFromError() { return { hasError: true }; }
    componentDidCatch(error, info) { console.error('Card render crashed:', error, info); }
    render() {
        if (this.state.hasError) return null;
        return this.props.children;
    }
}

// Normalize Route shorthands to database match values
const normalizePostType = (type) => {
    if (!type) return '';
    const t = String(type).trim().toLowerCase();
    if (t === 'ap' || t === 'andhra pradesh') return 'Andhra Pradesh';
    if (t === 'ts' || t === 'telangana') return 'Telangana';
    if (t === 'india') return 'India';
    if (t === 'world') return 'World';
    return type;
};

const ViewAllPage = ({ setActiveCommentPostId }) => {
    const navigate = useNavigate();
    const { category: encodedPostType } = useParams();
    const [mixedContent, setMixedContent] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchParams] = useSearchParams();
    const [fetchError, setFetchError] = useState(null);

    const [activeFilterCardId, setActiveFilterCardId] = useState(null);

    const isFetching = useRef(false);
    const lastTimestampRef = useRef(null);
    const [hasMore, setHasMore] = useState(true);
    const tabsContainerRef = useRef(null);
    const [fullImageUrl, setFullImageUrl] = useState(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const activeIndexRef = useRef(0);
    const animatingRef = useRef(false);
    const stageRef = useRef(null);
    const cardRefsMap = useRef({});
    const mixedContentRef = useRef([]);
    const fetchNewsRef = useRef(null);
    // --- ADD THIS REF FOR PEEK ANIMATION GUARD ---
    const hasPeekedRef = useRef(false);

    // Dynamic Quotes Pool State matching Home.js setup
    const [quotesPool, setQuotesPool] = useState([]);

    useEffect(() => {
        if (!window.Capacitor) return;
        const listener = App.addListener('backButton', ({ canGoBack }) => {
            canGoBack ? window.history.back() : navigate('/');
        });
        return () => {
            listener.then(l => {
             if (l && typeof l.remove === 'function') {
                 l.remove();
             }
            }).catch(err => console.warn("Failed to remove backButton listener:", err));
        };
    }, [navigate]);
    // Firestore quotes synchronizer
    useEffect(() => {
        const db = getFirestore(app);
        const unsubscribe = onSnapshot(collection(db, "quotes"), (snapshot) => {
            const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setQuotesPool(list);
            syncQuotesFromFirestore(list);
        }, (error) => {
            console.error("Quotes collection sync error:", error);
        });

        return () => unsubscribe();
    }, []);
    // Get all initial filters from the URL to construct the API request
    const initialFilters = useMemo(() => ({
        postType: decodeURIComponent(encodedPostType) || 'All',
        state: searchParams.get('state') || '',
        district: searchParams.get('district') || '',
        constituency: searchParams.get('constituency') || '',
        mandal: searchParams.get('mandal') || '',
    }), [encodedPostType, searchParams]);

    const [pageFilters, setPageFilters] = useState({
        subCategory: null,
        isTopNews: false,
        state: initialFilters.state, 
        district: initialFilters.district,
        constituency: initialFilters.constituency,
        mandal: initialFilters.mandal,
    });

    const postTypeFromURL = decodeURIComponent(encodedPostType);
    const isWishesPage = 
        postTypeFromURL === SPECIAL_WISHES_POST_TYPE || 
        postTypeFromURL === "Special Day Wishes" || 
        postTypeFromURL === "SpecialDayWishes" ||
        postTypeFromURL === "Wishes" ||
        postTypeFromURL === "ప్రత్యేక శుభాకాంక్షలు" ||
        postTypeFromURL.toLowerCase().includes("wish");

    // actual genres list for category filters
    const categoriesTabs = useMemo(() => [
        { id: 'top-news', label: 'టాప్ వార్తలు (Top News)', value: 'TopNews', isTopNews: true, subCategory: null },
        { id: 'all', label: 'అన్ని విభాగాలు (All)', value: 'All', isTopNews: false, subCategory: null },
        { id: 'politics', label: 'రాజకీయాలు (Politics)', value: 'Politics', isTopNews: false, subCategory: 'Politics' },
        { id: 'business', label: 'వ్యాపారం (Business)', value: 'Business', isTopNews: false, subCategory: 'Business' },
        { id: 'crime', label: 'క్రైమ్ (Crime)', value: 'Crime', isTopNews: false, subCategory: 'Crime' },
        { id: 'technology', label: 'టెక్నాలజీ (Technology)', value: 'Technology', isTopNews: false, subCategory: 'Technology' },
        { id: 'agriculture', label: 'వ్యవసాయం (Agriculture)', value: 'Agriculture', isTopNews: false, subCategory: 'Agriculture' },
        { id: 'entertainment', label: 'వినోదం (Entertainment)', value: 'Entertainment', isTopNews: false, subCategory: 'Entertainment' },
        { id: 'sports', label: 'క్రీడలు (Sports)', value: 'Sports', isTopNews: false, subCategory: 'Sports' },
        { id: 'health', label: 'ఆరోగ్యం (Health)', value: 'Health', isTopNews: false, subCategory: 'Health' }
    ], []);

    // Active tab value verification helper
    const currentActiveTab = useMemo(() => {
        if (pageFilters.isTopNews) {
            return 'TopNews';
        }
        if (!pageFilters.subCategory) {
            return 'All';
        }
        return pageFilters.subCategory.value;
    }, [pageFilters.isTopNews, pageFilters.subCategory]);

    const stateOptions = useMemo(() => locationData.map(s => ({ value: s.state_en, label: s.state_te })), []);
    
    const districtOptions = useMemo(() => {
        if (!pageFilters.state) return [];
        const stateData = locationData.find(s => s.state_en === pageFilters.state);
        return stateData ? stateData.districts.map(d => ({ value: d.district_en, label: d.district_te })) : [];
    }, [pageFilters.state]);

    const constituencyOptions = useMemo(() => {
        if (!pageFilters.state || !pageFilters.district) return []; 
        const stateData = locationData.find(s => s.state_en === pageFilters.state);
        const districtData = stateData?.districts.find(d => d.district_en === pageFilters.district);
        return districtData?.constituencies 
            ? districtData.constituencies.map(c => ({ value: String(c.value), label: String(c.label) })) 
            : []; 
    }, [pageFilters.state, pageFilters.district]);

    const mandalOptions = useMemo(() => {
        if (!pageFilters.state || !pageFilters.district) return []; 
        const stateData = locationData.find(s => s.state_en === pageFilters.state);
        const districtData = stateData?.districts.find(d => d.district_en === pageFilters.district);
        if (!districtData?.mandals) return [];
        
        return districtData.mandals.map(m => {
            if (typeof m === 'object' && m !== null) {
                return { 
                    value: String(m.value || m.mandal_en || ''), 
                    label: String(m.label || m.mandal_te || '') 
                };
            }
            return { value: String(m), label: String(m) };
        });
    }, [pageFilters.state, pageFilters.district]);

    // Premium horizontal select pills matching category style
    const pillSelectStyles = {
        control: (provided, state) => {
            const hasValue = state.hasValue;
            return {
                ...provided,
                backgroundColor: hasValue ? 'transparent' : 'rgba(255,255,255,0.06)',
                backgroundImage: hasValue ? 'linear-gradient(135deg, #ff9800, #ff5722)' : 'none',
                border: hasValue ? 'none' : '1px solid rgba(255,255,255,0.15)',
                borderRadius: '24px',
                minHeight: '34px',
                height: '34px',
                padding: '0 8px 0 12px',
                boxShadow: 'none',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                flexWrap: 'nowrap',
                transition: 'all 0.25s ease',
                '&:hover': {
                    backgroundColor: hasValue ? 'transparent' : 'rgba(255,255,255,0.12)',
                    borderColor: hasValue ? 'transparent' : 'rgba(255,255,255,0.3)',
                }
            };
        },
        valueContainer: (provided) => ({
            ...provided,
            padding: '0',
            margin: '0',
            display: 'flex',
            alignItems: 'center',
            height: '34px',
            overflow: 'visible',
        }),
        singleValue: (provided) => ({
            ...provided,
            color: '#ffffff',
            fontSize: '13.5px',
            fontWeight: '700',
            margin: '0',
            padding: '0',
        }),
        placeholder: (provided) => ({
            ...provided,
            color: 'rgba(255,255,255,0.65)',
            fontSize: '13.5px',
            fontWeight: '700',
            margin: '0',
            padding: '0',
        }),
        indicatorsContainer: (provided) => ({
            ...provided,
            height: '34px',
            padding: '0',
        }),
        indicatorSeparator: () => ({
            display: 'none'
        }),
        dropdownIndicator: (provided, state) => ({
            ...provided,
            padding: '0 2px',
            color: state.hasValue ? '#ffffff' : 'rgba(255,255,255,0.4)',
            '&:hover': { color: '#ffffff' }
        }),
        clearIndicator: (provided, state) => ({
            ...provided,
            padding: '0 2px',
            color: state.hasValue ? '#ffffff' : 'rgba(255,255,255,0.4)',
            '&:hover': { color: '#ffffff' }
        }),
        menu: (provided) => ({
            ...provided,
            backgroundColor: '#1e1e1e',
            borderRadius: '12px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.5)',
            border: '1px solid rgba(255,255,255,0.1)',
            overflow: 'hidden',
            zIndex: 9999,
            width: '200px',
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected 
                ? '#ff9800' 
                : state.isFocused 
                    ? 'rgba(255,255,255,0.08)' 
                    : 'transparent',
            color: '#ffffff',
            fontSize: '13.5px',
            fontWeight: '600',
            padding: '8px 12px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            '&:active': { backgroundColor: '#ff5722' }
        })
    };

    const fetchNews = useCallback(async (isLoadMore = false) => {
        if (isFetching.current || (isLoadMore && !hasMore)) return;
        isFetching.current = true;
        if (!isLoadMore) {
            setLoading(true);
            lastTimestampRef.current = null;
            setHasMore(true);
        }
        setFetchError(null);
    
        try {
            const params = { limit: 20 };

            if (isLoadMore && lastTimestampRef.current) {
                params.lastTimestamp = lastTimestampRef.current;
            }
        
            const matchedState = locationData.find(s => s.state_en === pageFilters.state);
            const stateTe = matchedState ? matchedState.state_te : '';
            
            const matchedDistrict = matchedState?.districts.find(d => d.district_en === pageFilters.district);
            const districtTe = matchedDistrict ? matchedDistrict.district_te : '';

            if (pageFilters.state) {
                params.state = pageFilters.state;
                params.stateEn = pageFilters.state;
                params.stateTe = stateTe;
            }
            if (pageFilters.district) {
                params.district = pageFilters.district;
                params.districtEn = pageFilters.district;
                params.districtTe = districtTe;
            }
            if (pageFilters.constituency) {
                params.constituency = pageFilters.constituency;
            }
            if (pageFilters.mandal) {
                params.mandal = pageFilters.mandal;
            }
            if (pageFilters.subCategory) {
                params.subCategory = pageFilters.subCategory.value;
            }

            // Normalizing routing variables to DB format
            if (!isWishesPage && postTypeFromURL && postTypeFromURL !== 'All' && postTypeFromURL !== 'Local') {
                params.postType = normalizePostType(postTypeFromURL);
            }

            if (pageFilters.isTopNews === true) {
                params.isTopNews = 'true';
            }

            let rawNews = [];
            const adsPromise = apiClient.get('/ads', { params }).catch(err => {
                console.warn("Ads failed to load for this category:", err);
                return { data: [] };
            });

            if (isWishesPage) {
                const wishesResponse = await apiClient.get('/user-news', { 
                    params: { ...params, postType: 'Special Day Wishes' } 
                });
                rawNews = wishesResponse.data;
            } else {
                const newsResponse = await apiClient.get('/user-news', { params });
                rawNews = newsResponse.data;
            }

            const [adsResponse] = await Promise.all([adsPromise]);

            if (rawNews.length < 20) setHasMore(false);

            if (rawNews.length > 0) {
                lastTimestampRef.current = rawNews[rawNews.length - 1].timestamp;

                const processedNews = rawNews.map(item => ({
                    ...item,
                    timestamp: item.timestamp?._seconds 
                        ? new Date(item.timestamp._seconds * 1000) 
                        : new Date(item.timestamp || Date.now())
                }));

                setMixedContent(prev => {
                    const existingNews = isLoadMore ? prev.filter(item => !item.isAd && !item.isAdRecommended) : [];
                    const totalNews = [...existingNews, ...processedNews];
                    // Passing an empty quotesPool array instead of a direct number to prevent type crashes
                    return isWishesPage ? totalNews : injectMixedContent(totalNews, adsResponse.data || [], quotesPool);
                });
            } else if (!isLoadMore) {
                setMixedContent([]);
                setHasMore(false);
            }

        } catch (error) {
            console.error("Error fetching news:", error);
            setFetchError("వార్తలను లోడ్ చేయడంలో విఫలమైంది.");
        } finally {
            setLoading(false);
            isFetching.current = false;
        }
    }, [encodedPostType, pageFilters, hasMore, isWishesPage, postTypeFromURL, quotesPool]);

    useEffect(() => {
        fetchNews(false);
    }, [
        encodedPostType,
        pageFilters.state,
        pageFilters.district,
        pageFilters.constituency,
        pageFilters.mandal,
        pageFilters.subCategory,
        pageFilters.isTopNews
    ]);

    useEffect(() => {
        mixedContentRef.current = mixedContent;
    }, [mixedContent]);

    useEffect(() => {
        fetchNewsRef.current = fetchNews;
    }, [fetchNews]);

    const applyStyles = useCallback((index, progress) => {
        const items = mixedContentRef.current;
        items.forEach((item, i) => {
            const key = item.adId || item.id || `slide-${i}`;
            const el = cardRefsMap.current[key];
            if (!el) return;
            const off = i - index;
            if (Math.abs(off) > 2) { el.style.display = 'none'; return; }
            el.style.display = 'block';
            if (off === 0) {
                const deg = progress * -90;
                const tz = Math.abs(progress) * -60;
                el.style.transform = `perspective(900px) rotateX(${deg}deg) translateZ(${tz}px)`;
                el.style.opacity = String(1 - Math.abs(progress) * 0.5);
                el.style.zIndex = '10';
            } else {
                const deg = 90 + progress * -90;
                const tz = Math.abs(progress) * -60;
                el.style.transform = `perspective(900px) rotateX(${deg}deg) translateZ(${tz}px)`;
                el.style.opacity = String(Math.abs(progress) * 0.9);
                el.style.zIndex = '5';
            }
        });
    }, []);

    const goNext = useCallback(() => {
        if (animatingRef.current) return;
        if (activeIndexRef.current >= mixedContentRef.current.length - 1) return;
        animatingRef.current = true;
        Object.values(cardRefsMap.current).forEach(el => {
            if (el) el.style.transition = 'transform 0.38s cubic-bezier(0.25,0.46,0.45,0.94), opacity 0.38s ease';
        });
        applyStyles(activeIndexRef.current, -1);
        setTimeout(() => {
            activeIndexRef.current += 1;
            setActiveIndex(activeIndexRef.current);
            Object.values(cardRefsMap.current).forEach(el => {
                if (el) el.style.transition = 'none';
            });
            applyStyles(activeIndexRef.current, 0);
            animatingRef.current = false;
            const threshold = mixedContentRef.current.length - 6;
            if (activeIndexRef.current >= threshold && !isFetching.current) {
                fetchNewsRef.current?.(true);
            }
        }, 380);
    }, [applyStyles]);

    const goPrev = useCallback(() => {
        if (animatingRef.current) return;
        if (activeIndexRef.current <= 0) return;
        animatingRef.current = true;
        Object.values(cardRefsMap.current).forEach(el => {
            if (el) el.style.transition = 'transform 0.38s cubic-bezier(0.25,0.46,0.45,0.94), opacity 0.38s ease';
        });
        activeIndexRef.current -= 1;
        setActiveIndex(activeIndexRef.current);
        applyStyles(activeIndexRef.current, 0);
        setTimeout(() => { animatingRef.current = false; }, 380);
    }, [applyStyles]);

    useEffect(() => {
        mixedContentRef.current = mixedContent;
        applyStyles(activeIndexRef.current, 0);
    }, [mixedContent, applyStyles]);

    useEffect(() => {
        const stage = stageRef.current;
        if (!stage) return;

        let startY = 0, lastDelta = 0;

        const onTouchStart = (e) => {
            startY = e.touches[0].clientY;
            lastDelta = 0;
            Object.values(cardRefsMap.current).forEach(el => {
                if (el) el.style.transition = 'none';
            });
        };
        const onTouchMove = (e) => {
            const dy = e.touches[0].clientY - startY;
            lastDelta = dy;
            const progress = Math.max(-1, Math.min(1, dy / stage.clientHeight));
            applyStyles(activeIndexRef.current, -progress);
        };
        const onTouchEnd = () => {
            if (lastDelta < -60) goNext();
            else if (lastDelta > 60) goPrev();
            else {
                Object.values(cardRefsMap.current).forEach(el => {
                    if (el) el.style.transition = 'transform 0.3s ease, opacity 0.3s ease';
                });
                applyStyles(activeIndexRef.current, 0);
            }
        };
        const onWheel = (e) => {
            e.preventDefault();
            if (e.deltaY > 25) goNext();
            else if (e.deltaY < -25) goPrev();
        };

        stage.addEventListener('touchstart', onTouchStart, { passive: true });
        stage.addEventListener('touchmove', onTouchMove, { passive: true });
        stage.addEventListener('touchend', onTouchEnd, { passive: true });
        stage.addEventListener('wheel', onWheel, { passive: false });

        return () => {
            stage.removeEventListener('touchstart', onTouchStart);
            stage.removeEventListener('touchmove', onTouchMove);
            stage.removeEventListener('touchend', onTouchEnd);
            stage.removeEventListener('wheel', onWheel);
        };
    }, [applyStyles, goNext, goPrev, loading, mixedContent]);

    const scrollSelectedTab = useCallback((index) => {
        if (tabsContainerRef.current) {
            const container = tabsContainerRef.current;
            const tabElement = container.children[index];
            if (tabElement) {
                const containerWidth = container.clientWidth;
                const tabWidth = tabElement.clientWidth;
                // Calculates absolute X scroll offsets relative to screen coordinate space
                const tabRect = tabElement.getBoundingClientRect();
                const containerRect = container.getBoundingClientRect();
                const absoluteTabX = tabRect.left - containerRect.left + container.scrollLeft;
                
                const targetScrollLeft = absoluteTabX - (containerWidth / 2) + (tabWidth / 2);
                
                // FIXED: Aligns the layout calculation exactly with the browser's painting thread
                requestAnimationFrame(() => {
                    container.scrollLeft = targetScrollLeft;
                });
            }
        }
    }, []);

    // FIXED: Schedules scroll centering only AFTER layout stability is achieved (loading is false)
    useEffect(() => {
        if (loading) return; 

        const activeIdx = categoriesTabs.findIndex(tab => tab.value === currentActiveTab);
        if (activeIdx !== -1) {
            const timer = setTimeout(() => {
                scrollSelectedTab(activeIdx);
            }, 100); 
            return () => clearTimeout(timer);
        }
    }, [currentActiveTab, loading, categoriesTabs, scrollSelectedTab]);

    // Auto-Scroll Peek Animation on Mount
    useEffect(() => {
        if (isWishesPage || hasPeekedRef.current) return;
        
        const peekTimer = setTimeout(() => {
            if (tabsContainerRef.current) {
                const container = tabsContainerRef.current;
                
                container.scrollTo({
                    left: 140, 
                    behavior: 'smooth'
                });
                
                setTimeout(() => {
                    const activeIdx = categoriesTabs.findIndex(tab => tab.value === currentActiveTab);
                    if (activeIdx !== -1) {
                        scrollSelectedTab(activeIdx);
                    } else {
                        container.scrollTo({
                            left: 0,
                            behavior: 'smooth'
                        });
                    }
                    hasPeekedRef.current = true;
                }, 900);
            }
        }, 1200);

        return () => clearTimeout(peekTimer);
    }, [isWishesPage, categoriesTabs, currentActiveTab, scrollSelectedTab]);

    const handleTabClick = (tab, index) => {
        setPageFilters(prev => ({
            ...prev,
            isTopNews: tab.isTopNews,
            subCategory: tab.subCategory ? { value: tab.subCategory, label: tab.label } : null
        }));
    };
    
    // if (loading) {
    //     return (
    //         <div className="view-all-page-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#121212' }}>
    //             <div className="fullscreen-loader" style={{ color: '#fff' }}>వార్తలను లోడ్ చేస్తోంది...</div>
    //         </div>
    //     );
    // }

    if (fetchError) return <div className="fullscreen-loader error">{fetchError}</div>;

    return (
        <div className={`view-all-page-container ${isWishesPage ? 'has-filters' : ''}`}>

            {/* Horizontal Categories and Top News Tab Bar (Hidden on Wishes Page) */}
            {!isWishesPage && (
                <div className="categories-tabs-ui" ref={tabsContainerRef}>
                    {categoriesTabs.map((tab, idx) => {
                        const isActive = currentActiveTab === tab.value;
                        return (
                            <button
                                key={tab.id}
                                className={`category-tab-btn ${isActive ? 'active' : ''}`}
                                onClick={() => handleTabClick(tab, idx)}
                            >
                                {tab.label}
                            </button>
                        );
                    })}
                </div>
            )}
            
            {/* Styled Filter Controls with Horizontal dark-mode pill design */}
            {isWishesPage && (
                <div 
                    className="wishes-filter-ui"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '8px 16px',
                        backgroundColor: '#121212',
                        borderBottom: '1px solid rgba(255,255,255,0.08)',
                        overflowX: 'auto',
                        whiteSpace: 'nowrap',
                        height: '52px',
                        flexShrink: 0,
                        boxSizing: 'border-box'
                    }}
                >
                    <Select
                        options={stateOptions}
                        value={stateOptions.find(s => s.value === pageFilters.state) || null}
                        onChange={selected => setPageFilters(prev => ({ ...prev, state: selected ? selected.value : '', district: '', constituency: '', mandal: '' }))}
                        placeholder="రాష్ట్రం (State)" isClearable
                        menuPortalTarget={document.body} 
                        styles={pillSelectStyles}
                    />

                    {pageFilters.state && (
                        <Select
                            options={districtOptions}
                            value={districtOptions.find(d => d.value === pageFilters.district) || null}
                            onChange={selected => setPageFilters(prev => ({ ...prev, district: selected ? selected.value : '', constituency: '', mandal: '' }))}
                            placeholder="జిల్ల్లా" isClearable
                            menuPortalTarget={document.body} 
                            styles={pillSelectStyles}
                        />
                    )}

                    {pageFilters.district && (
                        <Select
                            options={constituencyOptions}
                            value={constituencyOptions.find(c => c.value === pageFilters.constituency) || null}
                            onChange={selected => setPageFilters(prev => ({ ...prev, constituency: selected ? selected.value : '', mandal: '' }))}
                            placeholder="నియోజకవర్గం" isClearable
                            menuPortalTarget={document.body} 
                            styles={pillSelectStyles}
                        />
                    )}

                    {pageFilters.constituency && (
                        <Select
                            options={mandalOptions}
                            value={mandalOptions.find(m => m.value === pageFilters.mandal) || null}
                            onChange={selected => setPageFilters(prev => ({ ...prev, mandal: selected ? selected.value : '' }))}
                            placeholder="మండలం" isClearable
                            menuPortalTarget={document.body} 
                            styles={pillSelectStyles}
                        />
                    )}
                </div>
            )}

            {/* PERSISTENT VIEWPORT INNER RENDERING FOR UNINTERRUPTED TAB SCROLLING */}
            {loading ? (
                <div 
                    className="fullscreen-loader" 
                    style={{ 
                        flex: 1, 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        color: '#fff', 
                        background: '#121212',
                        width: '100%'
                    }}
                >
                    వార్తలను లోడ్ చేస్తోంది...
                </div>
            ) : mixedContent.length === 0 ? (
                <div className="no-data-found-fullscreen">
                    <i className="fas fa-newspaper" style={{ fontSize: '4rem', color: '#ddd' }}></i>
                    <h3>వార్తలు ఏవీ లేవు</h3>
                    <p>ఈ విభాగంలో ప్రస్తుతం వార్తలు అందుబాటులో లేవు.</p>
                    <button onClick={() => navigate('/')} className="button-primary">తిరిగి హోమ్ కి వెళ్ళు</button>
                </div>
            ) : (
                <div
                    className="cube-stage"
                    ref={stageRef}
                    style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}
                >
                    {fullImageUrl && ReactDOM.createPortal(
                        <div
                            onClick={() => setFullImageUrl(null)}
                            style={{
                                position: 'fixed', inset: 0,
                                background: 'rgba(0,0,0,0.96)',
                                zIndex: 999999,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                        >
                            <img
                                src={fullImageUrl}
                                alt="Full view"
                                style={{ maxWidth: '100%', maxHeight: '90vh', objectFit: 'contain' }}
                                onClick={e => e.stopPropagation()}
                            />
                            <span
                                onClick={() => setFullImageUrl(null)}
                                style={{
                                    position: 'absolute', top: 40, right: 20,
                                    color: '#fff', fontSize: 32,
                                    cursor: 'pointer', padding: '8px 12px',
                                }}
                            >✕</span>
                        </div>,
                        document.body
                    )}
                    {mixedContent.map((item, index) => {
                        const key = item.adId || item.id || `slide-${index}`;
                        const isActive = index === activeIndex;

                        const isWishesCard = (() => {
                            const checkMatch = (val) => {
                                if (!val) return false;
                                const checkStr = (str) => {
                                    const s = String(str).toLowerCase();
                                    return s.includes('wish') || s.includes('special') || s.includes('శుభాకాంక్షలు') || s.includes('పుట్టినరోజు');
                                };
                                if (Array.isArray(val)) {
                                    return val.some(item => checkStr(item));
                                }
                                return checkStr(val);
                            };

                            const hasExactMatch = (val) => {
                                if (!val) return false;
                                const targetTypes = ['special day wishes', 'specialdaywishes', 'wishes', 'ప్రత్యేక శుభాకాంక్షలు'];
                                if (Array.isArray(val)) {
                                    return val.some(item => targetTypes.includes(String(item).toLowerCase()));
                                }
                                return targetTypes.includes(String(val).toLowerCase());
                            };

                            return (
                                hasExactMatch(item.postType) ||
                                checkMatch(item.postType) ||
                                checkMatch(item.category) ||
                                checkMatch(item.title)
                            );
                        })();

                        return (
                            <div
                                key={key}
                                className="cube-slide"
                                ref={el => {
                                    if (el) cardRefsMap.current[key] = el;
                                    else delete cardRefsMap.current[key];
                                }}
                                style={{
                                    position: 'absolute', top: 0, left: 0,
                                    width: '100%', height: '100%',
                                    display: 'none', willChange: 'transform',
                                }}
                            >
                                <CardErrorBoundary>
                                {/* --- THE RENDER TREE LOOP --- */}
                                {item.isCustomQuote ? (
                                    <QuotesCard quotes={item.quotesData} />
                                ) : item.isAdRecommended ? (
                                    <AdRecommendedCard 
                                        adData={item.adData}
                                        recommendedNews={item.recommendedNews}
                                        isActive={isActive}
                                    />
                                ) : item.isAd ? (
                                    <AdCard adData={item} isActive={isActive} />
                                ) : isWishesCard ? (
                                    <WishesCard
                                        newsItem={{ ...item, description: item.content || item.description }}
                                        isActive={isActive}
                                        setActiveCommentPostId={setActiveCommentPostId}
                                    />
                                ) : (
                                    <NewsCard
                                        newsItem={item}
                                        isFeedView={true}
                                        pageFilters={pageFilters}
                                        setPageFilters={setPageFilters}
                                        activeFilterCardId={activeFilterCardId}
                                        setActiveFilterCardId={setActiveFilterCardId}
                                        setActiveCommentPostId={setActiveCommentPostId}
                                        isActive={isActive}
                                        onImageOpen={(url) => setFullImageUrl(url)}
                                    />
                                )}
                                </CardErrorBoundary>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ViewAllPage;